import React, { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { cn } from '../../../components/TwoDays/model/derive';
import { KST, formatClock } from '../../../components/TwoDays/model/time';
import { dur } from '../../../components/TwoDays/model/dashboard';
import { activityOf, versionsOf } from '../../../components/TwoDays/model/relay';
import useMedia, { MOBILE } from '../../../components/TwoDays/useMedia';
import useLiveBoard from '../../../components/TwoDays/ops/useLiveBoard';
import { actionsFor, dt, heroOf, hm, kstAt } from '../../../components/TwoDays/ops/liveBoardModel';
import { LogList, Pill, PillButton, SectionHead, TabPills, Tag, card, listBox } from '../../../components/TwoDays/ops/opsUi';
import { CallDialog, CancelDialog, DueDialog, ReassignDialog, ReceiverDialog } from '../../../components/TwoDays/ops/OpsDialogs';
import { ChevronRelay } from '../../../components/TwoDays/TaskRelay';
import { logEvents } from './OpsLiveBoard';

/**
 * Ops › Live board › 태스크 (O3, 시안 isTask) — 참여자 태스크 상세와 같은 뼈대.
 *   머리(← Live board · 팀 · 단계 · 제목 · 칩 · 화살표 릴레이) / 오른쪽 정보 카드(320px)
 *   Hero "Live board · intervene"(상태 · 지표 3칸 · 행동 — 주 행동 먼저, 막힌 것은 이유 툴팁)
 *   Work(읽기 전용 버전 목록) · Activity(All · Comments · Ops + Observation note)
 */

const HERO = {
  failed: ['border-functional-failed03/[0.24] bg-functional-failed01', 'bg-functional-failed03'],
  warning: ['border-[#ffdf56] bg-functional-warning01', 'bg-functional-warning03'],
  ok: ['border-green-deep03/[0.24] bg-surface-green10', 'bg-green-deep02'],
  closed: ['border-green-deep03/[0.12] bg-grayscale-gray00', 'bg-grayscale-gray05'],
};

const InfoRow = ({ label, sub, children, first }) => (
  <div className={cn('grid grid-cols-[112px_minmax(0,1fr)] gap-2 py-3 text-[14px] leading-[140%]', !first && 'border-t border-green-deep03/[0.08]')}>
    <span className='text-text-secondary'>{label}</span>
    <span className='flex min-w-0 flex-col gap-0.5 text-text-primary'>
      <span>{children}</span>
      {sub && <span className='text-[13px] text-text-secondary'>{sub}</span>}
    </span>
  </div>
);

const TABS = [['all', 'All'], ['comments', 'Comments'], ['ops', 'Ops']];

const OpsLiveTask = () => {
  const { taskId } = useParams();
  const { teams, logs, now, error, act, loading } = useLiveBoard();
  const mobile = useMedia(MOBILE);
  const desk = useMedia('(min-width: 1180px)');
  const [dialog, setDialog] = useState(null);
  const [tab, setTab] = useState('all');
  const [note, setNote] = useState('');
  const [noting, setNoting] = useState(false);
  const team = useMemo(() => teams?.find((t) => t.taskById.has(taskId)) ?? null, [teams, taskId]);
  const row = team?.taskRow(taskId) ?? null;
  const close = () => setDialog(null);

  if (loading || !teams) return <p className='py-10 text-center text-[14px] text-text-secondary'>{error || 'Loading…'}</p>;
  if (!team || !row) {
    return (
      <div className='flex flex-col gap-4'>
        <Link to='/2days/ops/live' className='text-[13px] text-text-secondary hover:text-text-primary'>← Live board</Link>
        <p className='text-[16px] font-medium text-text-primary'>Task not found</p>
      </div>
    );
  }

  const { t } = row;
  const name = team.name;
  const taskTo = (id) => `/2days/ops/live/tasks/${id}`;
  const hero = heroOf(row);
  const A = actionsFor(row, team.data.members);
  const am = row.assigneeRow;
  const closed = ['approved', 'cancelled'].includes(t.state);

  // ── 릴레이 — 워크보드 태스크 페이지와 같은 스테퍼 (앞 단계 · 이 태스크 · 다음 단계)
  const prevs = (t.predecessorIds ?? []).map((id) => team.taskById.get(id)).filter(Boolean);
  const nexts = team.data.tasks.filter((x) => (x.predecessorIds ?? []).includes(t.id) && x.state !== 'cancelled');
  const relayItem = (x, label) => {
    const r = team.taskRow(x.id);
    const ok = x.state === 'approved';
    return { key: x.id, kind: 'node', label, title: x.title, meta: `${r.assigneeName} · ${ok && x.review?.at ? `Approved ${dt(x.review.at)}` : r.chip.label}`, metaOk: ok, to: taskTo(x.id) };
  };
  const relay = [
    ...(prevs.length ? prevs.map((x) => relayItem(x, 'Previous')) : [{ key: 'start', kind: 'end', label: 'Start of relay', meta: 'No earlier task' }]),
    { key: 'cur', kind: 'cur', label: 'This task', title: `${row.assigneeName} → ${row.receiverName}`, meta: row.chip.label },
    ...(nexts.length ? nexts.map((x) => relayItem(x, 'Next')) : [{ key: 'end', kind: 'end', label: t.isFinal ? 'Team lead Kim' : 'End of relay', meta: t.isFinal ? 'Final review' : 'No later task' }]),
  ];

  // ── 행동 버튼 — 주 행동 먼저 (시안 btns)
  const B = (key, label, kind, primary, danger) => {
    const a = A[key] ?? { disabled: false, tip: '' };
    return { key, label, kind, primary: !!primary && !a.disabled, disabled: a.disabled, tip: a.tip, danger };
  };
  const btns = [];
  if (A.call.show) btns.push({ key: 'call', label: A.call.label, kind: 'call', primary: A.call.primary, disabled: false, tip: '', danger: false });
  btns.push(B('reassign', 'Reassign', 'reassign', A.reassign.primary), B('due', 'Change due', 'due', false), B('receiver', 'Change receiver', 'receiver', A.receiver.primary), B('cancel', 'Cancel task', 'cancel', false, true));
  btns.sort((a, b) => (b.primary ? 1 : 0) - (a.primary ? 1 : 0));

  const waitV = row.wait ? (row.wait.paused ? 'Paused · offline' : dur(row.wait.min)) : t.acceptedAt ? 'Accepted' : '–';
  const metrics = [
    ['Accept wait', waitV, row.wait && !row.wait.paused ? (row.wait.lvl === 'fail' ? 'text-functional-failed03' : row.wait.lvl === 'warn' ? 'text-functional-warning04' : 'text-text-primary') : 'text-text-secondary'],
    ['Assignee', am ? (am.presence.kind === 'dropped' ? 'Dropped' : am.presence.kind === 'active' ? 'Active' : am.presence.label.replace('Offline · back', 'Offline ·')) : '—', am?.presence.kind === 'dropped' ? 'text-functional-failed03' : 'text-text-primary'],
    ['Due', `${mobile ? hm(t.dueAt) : dt(t.dueAt)}${t.extendedReason ? ' · changed' : ''}`, 'text-text-primary'],
  ];

  // ── Work — 버전 목록
  const versions = versionsOf(t);
  const vers = versions.map((v) => {
    const d = v.deliverable ?? {};
    const r = v.review;
    const chip = r?.result === 'approved' ? { label: 'Approved', tone: 'success' } : r ? { label: r.returned ? 'Returned' : 'Changes requested', tone: 'warning' } : { label: 'In review', tone: 'accent' };
    const meta = `Submitted ${d.submittedAt ? dt(d.submittedAt) : '–'} · ${row.receiverName}${r?.at ? ` ${r.result === 'approved' ? 'approved' : r.returned ? 'returned' : 'requested changes'} ${dt(r.at)}` : ''}`;
    return { v: v.v, chip, meta, flag: row.toOps && !r ? 'Waiting for Team lead Kim' : '', links: (d.slots ?? []).filter((s) => s.url).map((s) => ({ l: s.l, url: s.url })), summary: d.summary ?? '' };
  });

  // ── Activity — 태스크 기록 + Ops 기록, 최신순 (시안 F)
  const leader = team.members.find((m) => m.leader)?.m;
  const events = activityOf({ task: t, comments: team.comments, name, leaderName: leader?.name ?? 'PM', viewerId: 'npc', seeExpected: true, fmt: (d) => dt(d) }).map((e) => ({ ...e, comment: ['question', 'comment', 'reply', 'blocker', 'rejected', 'returned'].includes(e.k) }));
  const commentEvents = team.comments
    .filter((c) => c.taskId === t.id && !['rejection', 'return'].includes(c.type))
    .map((c) => ({ n: `c-${c.id}`, at: c.createdAt, who: name(c.authorId), title: c.parentId ? 'Replied' : c.type === 'question' ? `Asked ${name(c.targetUserId)} a question` : c.type === 'blocker' ? 'Marked as blocked' : 'Commented', detail: c.body ? `“${c.body}”` : '', key: !c.parentId, comment: true, time: dt(c.createdAt) }));
  const opsEvents = logEvents(logs.filter((l) => l.taskId === t.id)).map((e) => ({ ...e, key: e.key && !e.opsOnly, tint: true }));
  const all = [...events.filter((e) => !['question', 'comment', 'reply'].includes(e.k)), ...commentEvents, ...opsEvents].sort((a, b) => new Date(b.at ?? 0) - new Date(a.at ?? 0));
  const F = { all, comments: all.filter((e) => e.comment), ops: all.filter((e) => e.ops) };
  const tabs = TABS.map(([k, l]) => [k, `${l} · ${F[k].length}`]);

  const addNote = async () => {
    setNoting(true);
    const ok = await act({ type: 'note', taskId: t.id, teamId: team.team.id, body: note.trim() });
    setNoting(false);
    if (ok) setNote('');
  };

  const info = (
    <aside className={cn(card, 'flex min-w-0 flex-col px-4 py-1')}>
      <InfoRow first label='Assignee' sub={am ? (am.presence.kind === 'dropped' ? `Dropped${am.dropped?.droppedAt ? ` ${hm(am.dropped.droppedAt)}` : ''}` : am.presence.kind === 'active' ? 'Active now' : am.presence.label) : null}>
        {row.assigneeName}
      </InfoRow>
      <InfoRow label='Receiver' sub={row.toOps ? 'Final review · Lead inbox' : row.receiverRow?.presence.kind === 'dropped' ? 'Dropped' : null}>{row.receiverName}</InfoRow>
      <InfoRow label='Due' sub={[row.adjusted ? `Adjusted due ${kstAt(row.adjusted)} · +${dur((row.adjusted.getTime() - new Date(t.dueAt).getTime()) / 60_000)} from previous step` : '', t.extendedReason ? 'Changed by ops · left out of on-time count' : ''].filter(Boolean).join(' · ') || null}>
        {kstAt(t.dueAt)}
      </InfoRow>
      <InfoRow label='Stage'>{row.stage ?? (t.isFinal ? 'Final' : '—')}</InfoRow>
      <InfoRow label='Accept wait' sub={t.assignedAt && row.wait ? `Since ${dt(t.assignedAt)}` : null}>{waitV}</InfoRow>
    </aside>
  );

  return (
    <div className={cn('grid items-start', desk ? 'grid-cols-[minmax(0,1fr)_320px] gap-x-10 gap-y-8' : 'grid-cols-1 gap-5')}>
      <header className='flex min-w-0 flex-col gap-3'>
        <Link to={`/2days/ops/live/${team.team.id}`} className='w-fit text-[13px] text-text-secondary hover:text-text-primary'>← Live board</Link>
        <span className='text-[13px] leading-[140%] text-text-secondary'>
          Team {team.team.name} · {row.stage ? `${row.stage} stage` : t.isFinal ? 'Final' : 'Task'}
        </span>
        <h1 className={cn('m-0 font-medium leading-[120%] tracking-[-0.01em] text-text-primary [text-wrap:pretty]', mobile ? 'text-[24px]' : 'text-[28px]')}>{t.title}</h1>
        <div className='flex flex-wrap gap-1.5'>
          {row.chips.map((c) => (
            <Pill key={c.label} tone={c.tone}>{c.label}</Pill>
          ))}
        </div>
        <ChevronRelay items={relay} />
      </header>

      {desk && <div className='row-span-2'>{info}</div>}

      <div className={cn('flex min-w-0 flex-col', mobile ? 'gap-7' : 'gap-10')}>
        {/* Hero — Live board · intervene */}
        <section aria-label='Ops actions' className={cn('flex flex-col gap-[18px] rounded-2xl border-[1.6px] shadow-4', mobile ? 'p-4' : 'px-6 py-5', HERO[hero.tone][0])}>
          <div className='flex flex-wrap items-center justify-between gap-x-3 gap-y-2'>
            <span className='flex items-center gap-2'>
              <span className='inline-flex h-[22px] items-center rounded-full bg-green-deep04 px-[9px] text-[12px] font-medium text-grayscale-gray00'>Ops</span>
              <span className='text-[13px] font-medium leading-[140%] text-text-secondary'>Live board · intervene</span>
            </span>
            <span className='text-[13px] leading-[140%] tabular-nums text-text-secondary'>Now {formatClock(now, KST)} KST</span>
          </div>
          <div className='flex items-start gap-3'>
            <span className={cn('mt-2 h-2.5 w-2.5 shrink-0 rounded-full', HERO[hero.tone][1])} />
            <div className='flex min-w-0 flex-col gap-1'>
              <span className={cn('font-medium leading-[130%] text-text-primary [text-wrap:pretty]', mobile ? 'text-[18px]' : 'text-[20px]')}>{hero.title}</span>
              <span className='text-[14px] leading-[150%] text-text-secondary [text-wrap:pretty]'>{hero.text}</span>
            </div>
          </div>
          <div className='grid grid-cols-3 overflow-hidden rounded-xl bg-green-deep03/[0.04]'>
            {metrics.map(([k, v, c], i) => (
              <div key={k} className={cn('flex min-w-0 flex-col gap-0.5', mobile ? 'px-3 py-2.5' : 'px-4 py-3', i > 0 && 'border-l border-green-deep03/[0.1]')}>
                <span className='text-[12px] leading-[140%] text-text-secondary'>{k}</span>
                <span className={cn('truncate font-medium leading-[140%] tabular-nums', mobile ? 'text-[14px]' : 'text-[16px]', c)}>{v}</span>
              </div>
            ))}
          </div>
          <div className='flex flex-wrap gap-2'>
            {btns.map((b) => (
              <PillButton key={b.key} size='l' variant={b.primary ? 'primary' : b.danger ? 'danger' : 'soft'} disabled={b.disabled} tip={b.tip} onClick={() => setDialog(b.kind)} style={mobile ? { flex: b.primary ? '1 1 100%' : '1 1 calc(50% - 4px)' } : undefined}>
                {b.label}
              </PillButton>
            ))}
          </div>
          <div className='flex flex-wrap items-center gap-x-2.5 gap-y-1.5'>
            {closed && <Tag>{t.state === 'approved' ? 'Closed · approved' : 'Closed · cancelled'}</Tag>}
            <span className='text-[12px] leading-[140%] text-text-tertiary'>Every action is written to the Activity log and sent to the people involved. Unavailable actions show why on hover.</span>
          </div>
        </section>

        {!desk && info}

        {/* Work */}
        <section className='flex flex-col gap-3'>
          <SectionHead title='Work' sub='Read-only for ops · the assignee and receiver act on the workboard' />
          {vers.length ? (
            <div className={listBox}>
              {vers.map((v, i) => (
                <div key={v.v} className={cn('flex flex-col gap-1 px-4 py-3', i > 0 && 'border-t border-green-deep03/[0.12]')}>
                  <div className='flex flex-wrap items-center gap-2'>
                    <span className='text-[14px] font-medium leading-[140%] text-text-primary'>v{v.v}</span>
                    <Pill tone={v.chip.tone}>{v.chip.label}</Pill>
                  </div>
                  <span className='text-[13px] leading-[150%] text-text-secondary'>{v.meta}</span>
                  {v.links.map((l) => (
                    <a key={l.url} href={l.url} target='_blank' rel='noreferrer' className='truncate text-[13px] text-text-primary hover:underline'>
                      <span className='font-medium'>{l.l}</span>
                      <span className='text-text-secondary'> · {l.url.replace(/^https?:\/\//, '')}</span>
                    </a>
                  ))}
                  {v.summary && <span className='whitespace-pre-line text-[13px] leading-[150%] text-text-secondary'>{v.summary}</span>}
                  {v.flag && (
                    <span className='flex items-center gap-1.5 text-[13px] leading-[150%] text-functional-warning04'>
                      <span className='h-1.5 w-1.5 rounded-full bg-functional-warning03' />
                      {v.flag} · ops only
                    </span>
                  )}
                </div>
              ))}
            </div>
          ) : (
            <div className='flex flex-col gap-1 rounded-xl border border-dashed border-green-deep03/[0.24] p-4'>
              <span className='text-[14px] font-medium leading-[140%] text-text-primary'>No submission yet</span>
              <span className='text-[13px] leading-[150%] text-text-secondary'>Versions show up here once the assignee submits.</span>
            </div>
          )}
        </section>

        {/* Activity */}
        <section className='flex flex-col gap-3'>
          <SectionHead title='Activity' sub='From request to now · newest first' right={<TabPills tabs={tabs} value={tab} onChange={setTab} />} />
          <div className={cn(listBox, 'gap-2.5 bg-grayscale-gray00 px-4 py-3.5')}>
            <span className='flex items-center gap-2 text-[13px] font-medium leading-[140%] text-text-primary'>
              Observation note
              <Tag title='Participants don’t see this'>Ops only</Tag>
            </span>
            <textarea value={note} onChange={(e) => setNote(e.target.value)} rows={2} maxLength={4000} placeholder='What did you see? Time is recorded automatically.' className='w-full resize-y rounded-xl border border-green-deep03/20 bg-grayscale-gray00 px-3 py-2.5 text-[14px] leading-[150%] text-text-primary placeholder:text-text-placeholder focus:border-green-deep02 focus:outline-none' />
            <div className='flex justify-end'>
              <PillButton size={mobile ? 'l' : 's'} variant='primary' disabled={!note.trim() || noting} tip='Write a note' onClick={addNote}>
                Save note
              </PillButton>
            </div>
          </div>
          <LogList events={F[tab]} empty={tab === 'ops' ? 'No ops actions on this task yet' : tab === 'comments' ? 'No comments yet' : 'Nothing yet'} />
        </section>
      </div>

      {dialog === 'call' && <CallDialog row={row} onAct={act} onClose={close} />}
      {dialog === 'reassign' && <ReassignDialog row={row} team={team} onAct={act} onClose={close} />}
      {dialog === 'receiver' && <ReceiverDialog row={row} team={team} onAct={act} onClose={close} />}
      {dialog === 'due' && <DueDialog row={row} endAt={team.data.project.endAt} onAct={act} onClose={close} />}
      {dialog === 'cancel' && <CancelDialog row={row} onAct={act} onClose={close} />}
    </div>
  );
};

export default OpsLiveTask;
